/**
 * Answer options: who references which value, and what it costs to change one.
 *
 * ## Why this needs the AST rather than a string search
 *
 * A value is referenced in three different shapes, and `'a'` appears in plenty of
 * expressions that have nothing to do with this question:
 *
 *     metric['Q_1_1_1'] === 'a'                       equality
 *     metric['Q_1_1_3'].includes('a')                 membership
 *     hasAnyMatch('Q_1_1_3_1', ['a','b','c'])         call, question named as a STRING
 *
 * Matching text would count `metric['Q_OTHER'] === 'a'` as a use of THIS question's
 * `a`, and would miss the third shape entirely unless it also knew that the first
 * argument of `hasAnyMatch` is a question name. Pairing a ref with a value is a
 * structural question, so it is answered structurally.
 *
 * ## What it is for
 *
 * Renaming or deleting an option value silently breaks every rule that matched on it:
 * the expression survives, reads `undefined`, and simply never fires again. The engine
 * raises nothing. So the editor has to say "3 rules match on this value" BEFORE the
 * delete, which is the one moment the information is useful.
 */
import type { QuestionnaireDefinition } from '../types';
import { type Expr, tryParse, walk } from './ast';

export interface OptionUse {
  /** Rules that decide the next question. */
  edges: number;
  /** Eligibility rules, evaluated at submit over the whole answer set. */
  programs: number;
  /** Client-side auto-fill rules. */
  autos: number;
  /** Where exactly, so the UI can name them rather than just count. */
  where: { nodeId: string; kind: 'edge' | 'program' | 'auto'; index: number }[];
}

const empty = (): OptionUse => ({ edges: 0, programs: 0, autos: 0, where: [] });

/** Does `e` test `ref` against `value`, in any of the three shapes? */
function testsValue(e: Expr, ref: string, value: string): boolean {
  return walk(e).some((n) => {
    if (n.k === 'cmp' && n.op === '===') {
      // Either side may hold the variable; the live data always puts it on the left.
      const pair = [
        [n.l, n.r],
        [n.r, n.l],
      ] as const;
      return pair.some(
        ([a, b]) => a.k === 'var' && a.ref === ref && b.k === 'lit' && b.v === value,
      );
    }
    if (n.k === 'has') return n.x.k === 'var' && n.x.ref === ref && n.v === value;
    if (n.k === 'anyMatch') return n.ref === ref && n.vs.includes(value);
    return false;
  });
}

/**
 * For each answer value of `nodeId`, every rule anywhere in the definition that tests
 * it.
 *
 * Scans the WHOLE definition, not just this node: an eligibility rule on a downstream
 * question routinely reads an upstream answer, and that is exactly the reference a
 * delete would break.
 */
export function optionUsage(
  def: QuestionnaireDefinition,
  nodeId: string,
): Map<string, OptionUse> {
  const node = def.nodes.find((n) => n.nodeId === nodeId);
  const out = new Map<string, OptionUse>();
  if (!node) return out;
  for (const o of node.answerOptions ?? []) out.set(o.value, empty());
  const ref = node.qualifiedNodeId;
  if (!ref) return out;

  const visit = (
    ownerId: string,
    kind: OptionUse['where'][number]['kind'],
    index: number,
    expression: string,
  ) => {
    const tree = tryParse(expression);
    // An unparseable expression is reported by the checker, not silently treated as
    // referencing nothing — but there is nothing useful this function can say about it.
    if (!tree) return;
    for (const [value, use] of out) {
      if (!testsValue(tree, ref, value)) continue;
      if (kind === 'edge') use.edges++;
      else if (kind === 'program') use.programs++;
      else use.autos++;
      use.where.push({ nodeId: ownerId, kind, index });
    }
  };

  for (const n of def.nodes) {
    (n.nextQuestionRules ?? []).forEach((r, i) => visit(n.nodeId, 'edge', i, r.expression));
    (n.programRules ?? []).forEach((r, i) => visit(n.nodeId, 'program', i, r.expression));
    (n.autoAnswerRules ?? []).forEach((r, i) => visit(n.nodeId, 'auto', i, r.expression));
  }
  return out;
}

export const totalUses = (u: OptionUse): number => u.edges + u.programs + u.autos;

/**
 * A value that is unique within the question, derived from the label.
 *
 * Authors should not have to invent these. Two live defects trace to hand-written
 * identifiers: `Q_1_1_3_1`'s multi-select validation had to key off the LABEL's first
 * letter because "value order differs from label order", and `Q1.1.7`'s `hasKey` name
 * differs from the real program name only in capitalisation.
 *
 * The live convention is a single letter per option (`a`, `b`, …) and that is what this
 * follows — matching the data beats being clever, because the rules the authors already
 * wrote read `=== 'a'`.
 */
export function freshOptionValue(existing: readonly string[]): string {
  const taken = new Set(existing);
  for (let i = 0; i < 26; i++) {
    const v = String.fromCharCode(97 + i);
    if (!taken.has(v)) return v;
  }
  // Past `z`, fall back to numbering rather than inventing a second alphabet.
  for (let i = 1; ; i++) {
    const v = `v${i}`;
    if (!taken.has(v)) return v;
  }
}

/** `optionId` is never referenced by a rule, so it only has to be unique and stable. */
export function freshOptionId(nodeId: string, existing: readonly string[]): string {
  const taken = new Set(existing);
  for (let i = 1; ; i++) {
    const id = `${nodeId}_o${i}`;
    if (!taken.has(id)) return id;
  }
}

/**
 * Does the option's label disagree with its value?
 *
 * The live labels are written `a) 全球区域` — the leading letter restates the value. When
 * the two drift apart, code that reads one while the rules read the other breaks:
 * `MultiSelectAnswer` validates `Q_1_1_3_1` by the label's first letter precisely
 * because "value order differs from label order" there. Worth a warning in the editor,
 * not an error — the convention is a convention, not a rule.
 */
export function labelDisagreesWithValue(label: string, value: string): boolean {
  const lead = /^\s*([A-Za-z0-9])\s*\)/.exec(label);
  return lead ? lead[1].toLowerCase() !== value.toLowerCase() : false;
}
